import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorCode } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EditorialButton } from "@/components/editorial";
import { ColumnInfo, DashCard, DashHeading } from "@/components/dashboard";

const ROLE_LABELS: Record<"owner" | "staff" | "viewer", string> = {
  owner: "owner (Administrador)",
  staff: "staff (Operador)",
  viewer: "viewer (Solo lectura)",
};

const ROLE_INFO = [
  {
    label: "owner (Administrador)",
    description: "Acceso completo: gestiona usuarios del panel, cambia roles, opera códigos y suscripciones, ve reportes. Debe haber al menos un owner activo por partner.",
  },
  {
    label: "staff (Operador)",
    description: "Opera el día a día: emite y anula códigos, revoca accesos, pausa/cancela suscripciones. NO puede gestionar usuarios del panel.",
  },
  {
    label: "viewer (Solo lectura)",
    description: "Solo lectura: puede ver códigos, suscripciones y reportes. No puede ejecutar ninguna acción.",
  },
];
import { cn } from "@/lib/utils";

type PartnerUser = {
  id: number;
  email: string;
  name: string;
  role: "owner" | "staff" | "viewer";
  is_active: boolean;
  has_password: boolean;
  created_at: string;
};

export default function UsersPage() {
  const currentUserId = useAuth((s) => s.user?.id);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<PartnerUser[]>({
    queryKey: ["users"],
    queryFn: async () => (await api.get<PartnerUser[]>("/partner/users")).data,
  });

  const patch = useMutation({
    mutationFn: async (input: { id: number; body: Record<string, unknown> }) =>
      (await api.patch(`/partner/users/${input.id}`, input.body)).data,
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      // Feedback contextual según qué se cambió
      if ("role" in variables.body) {
        toast.success("Rol actualizado");
      } else if ("is_active" in variables.body) {
        toast.success(variables.body.is_active ? "Usuario reactivado" : "Usuario desactivado");
      }
    },
    onError: () => toast.error("No se pudo actualizar el usuario"),
  });

  return (
    <div>
      <DashHeading
        title="Equipo"
        description="Personas con acceso al panel de tu partner. Los roles determinan qué puede hacer cada uno: owner gestiona usuarios y configura; staff opera códigos y grants; viewer solo consulta."
        actions={<InviteDialog />}
      />

      <DashCard bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50/50 border-b border-neutral-200">
              <tr className="text-left text-xs font-medium text-neutral-600">
                <th className="px-6 py-3">Nombre</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">
                  <span className="inline-flex items-center gap-1.5">
                    Rol
                    <ColumnInfo title="Roles del panel" items={ROLE_INFO} />
                  </span>
                </th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-sm text-neutral-500">
                    Cargando…
                  </td>
                </tr>
              )}
              {data?.map((u) => (
                <tr key={u.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-3 text-sm text-neutral-900">{u.name}</td>
                  <td className="px-6 py-3 text-xs text-neutral-600">{u.email}</td>
                  <td className="px-6 py-3">
                    <select
                      className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-sm focus:outline-none focus:border-neutral-400 disabled:opacity-50"
                      value={u.role}
                      disabled={u.id === currentUserId}
                      onChange={(e) => patch.mutate({ id: u.id, body: { role: e.target.value } })}
                    >
                      <option value="owner">{ROLE_LABELS.owner}</option>
                      <option value="staff">{ROLE_LABELS.staff}</option>
                      <option value="viewer">{ROLE_LABELS.viewer}</option>
                    </select>
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill
                      label={u.is_active ? (u.has_password ? "Activo" : "Invitado") : "Inactivo"}
                      tone={u.is_active ? (u.has_password ? "green" : "blue") : "gray"}
                    />
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap space-x-2">
                    {u.is_active && !u.has_password && (
                      <RegenerateInviteButton userId={u.id} />
                    )}
                    {u.id !== currentUserId && u.is_active && (
                      <EditorialButton
                        variant="ghost"
                        size="sm"
                        onClick={() => patch.mutate({ id: u.id, body: { is_active: false } })}
                      >
                        Desactivar
                      </EditorialButton>
                    )}
                    {!u.is_active && (
                      <EditorialButton
                        variant="ghost"
                        size="sm"
                        onClick={() => patch.mutate({ id: u.id, body: { is_active: true } })}
                      >
                        Reactivar
                      </EditorialButton>
                    )}
                  </td>
                </tr>
              ))}
              {data && data.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-sm text-neutral-500">
                    Aún no hay usuarios. Invita al primero.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DashCard>
    </div>
  );
}

function StatusPill({ label, tone }: { label: string; tone: "green" | "blue" | "gray" }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
        tone === "green" && "bg-emerald-50 text-emerald-700 border border-emerald-100",
        tone === "blue" && "bg-blue-50 text-blue-700 border border-blue-100",
        tone === "gray" && "bg-neutral-50 text-neutral-600 border border-neutral-200"
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          tone === "green" && "bg-emerald-500",
          tone === "blue" && "bg-blue-500",
          tone === "gray" && "bg-neutral-400"
        )}
      />
      {label}
    </span>
  );
}

function InviteDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"owner" | "staff" | "viewer">("staff");
  const [err, setErr] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post<{ invite_url: string }>("/partner/users", { email, name, role })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setInviteUrl(data.invite_url);
      setEmail(""); setName("");
      toast.success("Invitación creada", { description: "Copia el enlace y compártelo." });
    },
    onError: (e) => {
      const c = apiErrorCode(e);
      const msg = c === "EMAIL_ALREADY_EXISTS" ? "Ya existe un usuario con ese email" : "No se pudo invitar";
      setErr(msg);
      toast.error(msg);
    },
  });

  function reset() {
    setInviteUrl(null); setErr(null); setEmail(""); setName(""); setRole("staff");
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <EditorialButton variant="primary">
          Invitar usuario <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
        </EditorialButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {inviteUrl ? "Invitación creada" : "Invitar al panel"}
          </DialogTitle>
          <DialogDescription>
            {inviteUrl
              ? "Copia este enlace y compártelo con la persona invitada."
              : "Se generará un enlace de activación."}
          </DialogDescription>
        </DialogHeader>

        {inviteUrl ? (
          <div className="space-y-3 mt-2">
            <div className="flex gap-2">
              <Input readOnly value={inviteUrl} onFocus={(e) => e.currentTarget.select()} className="flex-1" />
              <CopyInviteButton url={inviteUrl} />
            </div>
            <DialogFooter>
              <DialogClose asChild><EditorialButton variant="primary">Cerrar</EditorialButton></DialogClose>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Nombre">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field
              label="Rol"
              labelExtra={<ColumnInfo title="Roles del panel" items={ROLE_INFO} />}
            >
              <select
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm focus:outline-none focus:border-neutral-400"
                value={role}
                onChange={(e) => setRole(e.target.value as typeof role)}
              >
                <option value="owner">{ROLE_LABELS.owner}</option>
                <option value="staff">{ROLE_LABELS.staff}</option>
                <option value="viewer">{ROLE_LABELS.viewer}</option>
              </select>
            </Field>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <DialogFooter className="mt-2 gap-2">
              <DialogClose asChild><EditorialButton variant="ghost">Cancelar</EditorialButton></DialogClose>
              <EditorialButton
                variant="primary"
                disabled={!email || !name || mutation.isPending}
                onClick={() => { setErr(null); mutation.mutate(); }}
              >
                {mutation.isPending ? "Enviando" : "Crear invitación"}
                <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
              </EditorialButton>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RegenerateInviteButton({ userId }: { userId: number }) {
  const [open, setOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post<{ invite_url: string }>(`/partner/users/${userId}/regenerate-invite`)).data,
    onSuccess: (data) => {
      setInviteUrl(data.invite_url);
      setErr(null);
      toast.success("Nuevo enlace generado", {
        description: "El enlace anterior quedó invalidado.",
      });
    },
    onError: (e) => {
      const c = apiErrorCode(e);
      let msg = "No se pudo regenerar el enlace";
      if (c === "USER_ALREADY_ACTIVATED") msg = "El usuario ya activó su cuenta";
      else if (c === "USER_INACTIVE") msg = "El usuario está desactivado";
      setErr(msg);
      toast.error(msg);
    },
  });

  function handleOpen(v: boolean) {
    setOpen(v);
    if (!v) {
      setInviteUrl(null);
      setErr(null);
    } else if (!inviteUrl) {
      mutation.mutate();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <EditorialButton variant="ghost" size="sm">Regenerar enlace</EditorialButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            Nuevo enlace de invitación
          </DialogTitle>
          <DialogDescription>
            El enlace anterior queda invalidado. Comparte este nuevo enlace con la persona invitada.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {mutation.isPending && (
            <p className="text-sm text-neutral-500">Generando enlace…</p>
          )}
          {err && <p className="text-sm text-destructive">{err}</p>}
          {inviteUrl && (
            <div className="flex gap-2">
              <Input
                readOnly
                value={inviteUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1"
              />
              <CopyInviteButton url={inviteUrl} />
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <EditorialButton variant="primary">Cerrar</EditorialButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyInviteButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: seleccionar el input adyacente si el clipboard API no está disponible
      const input = document.querySelector<HTMLInputElement>('input[readonly]');
      if (input) {
        input.focus();
        input.select();
      }
    }
  }

  return (
    <EditorialButton
      type="button"
      variant={copied ? "primary" : "outline"}
      onClick={handleCopy}
      aria-label="Copiar enlace de invitación"
    >
      {copied ? (
        <>
          <Check className="h-4 w-4" strokeWidth={2} />
          Copiado
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" strokeWidth={2} />
          Copiar
        </>
      )}
    </EditorialButton>
  );
}

function Field({
  label,
  labelExtra,
  children,
}: {
  label: string;
  labelExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="inline-flex items-center gap-1.5">
        <Label className="text-sm font-medium text-neutral-700">{label}</Label>
        {labelExtra}
      </div>
      {children}
    </div>
  );
}
